import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Text,
  Title,
  Group,
  Stack,
  Badge,
  Button,
  SimpleGrid,
  Center,
  useMantineColorScheme,
  Box,
  Loader,
  List,
  Accordion,
  Anchor,
} from '@mantine/core';
import PageTransition from '../components/animations/PageTransition';
import SimpleNavbar from '../components/SimpleNavbar';
import {
  IconChecks,
  IconBrandInstagram,
  IconMail,
  IconStethoscope,
  IconAdjustmentsHorizontal,
  IconChartHistogram,
  IconArrowRight,
  IconStar,
} from '@tabler/icons-react';
import enarmLogo from '../assets/enarm_logo.png';
import { subscriptionPlanService, SubscriptionPlanDTO } from '../services/subscriptionPlanService';
import { authService } from '../services/authService';

// ============================================================
// Datos estáticos (copy real conservado del diseño anterior)
// ============================================================
const STATS = [
  { number: '27+', label: 'Especialidades' },
  { number: '15K+', label: 'Reactivos' },
  { number: '87%', label: 'Aprobación' },
  { number: '24/7', label: 'Soporte' },
];

const FEATURES = [
  {
    icon: IconStethoscope,
    title: 'Especialidades Completas',
    description:
      'Toda la gama completa de especialidades del padrón oficial del ENARM. Contenido actualizado y estructurado por expertos.',
  },
  {
    icon: IconAdjustmentsHorizontal,
    title: 'Personalización Total',
    description:
      'Adapta cada espacio de tu perfil conforme a tus necesidades, intereses y objetivos específicos de preparación.',
  },
  {
    icon: IconChartHistogram,
    title: 'Herramientas Avanzadas',
    description:
      'Clases grabadas, asesorías en línea, medición de progreso, sistema de recomendaciones y libreta digital colaborativa.',
  },
];

const FAQS = [
  {
    value: 'trial',
    q: '¿Puedo solicitar un período de prueba?',
    a: 'Así es, al momento de ser un nuevo usuario, se te brinda un período de prueba de 15 días para que conozcas la plataforma, te puedas familiarizar y estructures los espacios que te servirán para tu estudio y desarrollo al ENARM.',
  },
  {
    value: 'educational-material',
    q: '¿Qué aval tiene el material educativo?',
    a: 'Ofrecemos material educativo basado en las guías oficiales de ediciones anteriores del ENARM. De la misma manera, buscamos asesoramiento con especialistas y alianzas con instituciones que nos permitan generar todo el contenido necesario para una preparación más completa, integral, interactiva y propositiva a tu perfil.',
  },
  {
    value: 'platform-access',
    q: '¿Cómo accedo a la plataforma después de suscribirme?',
    a: 'Una vez completado tu registro y pago, recibirás inmediatamente tus credenciales de acceso por email. Podrás acceder desde cualquier dispositivo las 24 horas del día, 7 días a la semana.',
  },
  {
    value: 'limited-access',
    q: '¿Qué incluye el acceso limitado a la plataforma?',
    a: 'Durante tu período de prueba tendrás acceso limitado a la plataforma que incluye: hasta 50 preguntas por día, acceso a 3 especialidades principales, reportes básicos de progreso y soporte por email. Esto te permite conocer las funcionalidades principales antes de suscribirte al plan completo.',
  },
  {
    value: 'support',
    q: '¿Qué tipo de soporte técnico ofrecen?',
    a: 'Contamos con un equipo de soporte dedicado disponible por email, chat en vivo y videollamadas. Los usuarios Premium tienen acceso a soporte prioritario 24/7 con tiempo de respuesta menor a 2 horas.',
  },
];

const intervalLabel = (interval?: string) => {
  switch (interval) {
    case 'DAILY':
      return 'día';
    case 'WEEKLY':
      return 'semana';
    case 'MONTHLY':
      return 'mes';
    case 'QUARTERLY':
      return 'trimestre';
    case 'SEMIANNUAL':
      return 'semestre';
    case 'YEARLY':
      return 'año';
    default:
      return 'mes';
  }
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';

  // ---- Tokens del sistema interno (coherencia con la app) ----
  const t = {
    bg: dark ? '#0f172a' : '#f7f3ee',
    surface: dark ? '#1e293b' : '#ffffff',
    surfaceAlt: dark ? '#172033' : '#fbf9f5',
    border: dark ? 'rgba(255,255,255,0.08)' : '#e9e4dc',
    text: dark ? '#e2e8f0' : '#2d2a26',
    muted: dark ? '#94a3b8' : '#8c8478',
    // Marca ENARM
    brand: 'rgb(54, 71, 91)', // azul-slate
    accent: 'rgb(196, 213, 70)', // verde-lima
    // CTA principal (negro de acento del interior / claro sobre dark)
    ctaBg: dark ? '#e2e8f0' : '#1a1714',
    ctaText: dark ? '#0f172a' : '#ffffff',
  };

  const display = 'Space Grotesk, Inter, sans-serif';
  const body = 'Inter, sans-serif';

  // Estado para los planes de suscripción
  const [plans, setPlans] = useState<SubscriptionPlanDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estado de autenticación
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [userRole, setUserRole] = useState<'student' | 'admin' | null>(null);

  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      const valid = await authService.validateOrRefreshSession();
      if (cancelled) return;
      const user = valid ? authService.getCurrentUserFromStorage() : null;
      setIsAuthenticated(!!valid && !!user);
      setUserInfo(user);
      if (valid && user) {
        if (authService.isAdmin()) setUserRole('admin');
        else if (authService.isEstudiante()) setUserRole('student');
      } else {
        setUserRole(null);
      }
    };
    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUserInfo(null);
      setUserRole(null);
      navigate('/login');
    } catch (err) {
      console.error('Error during logout:', err);
    }
  };

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        const activePlans = await subscriptionPlanService.getAllActivePlans();
        const sortedPlans = activePlans.sort((a, b) => Number(a.price) - Number(b.price));
        setPlans(sortedPlans);
        setError(null);
      } catch (e: any) {
        console.error('Error loading plans:', e);
        setError('No se pudieron cargar los planes');
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, []);

  const scrollToPrecios = () => {
    document.getElementById('precios')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // El plan "destacado" es el del medio si hay 3, si no el más caro.
  const popularIndex = plans.length >= 3 ? Math.floor(plans.length / 2) : plans.length - 1;

  const sectionLabel = (text: string) => (
    <Text
      component="span"
      style={{
        fontFamily: body,
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: t.brand,
      }}
    >
      {text}
    </Text>
  );

  return (
    <PageTransition>
      <style>{`
        @keyframes lp-rise {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .lp-rise { animation: lp-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .lp-card {
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .lp-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 34px rgba(54, 71, 91, 0.10);
        }
        @media (prefers-reduced-motion: reduce) {
          .lp-rise { animation: none; }
          .lp-card { transition: none; }
          .lp-card:hover { transform: none; }
        }
      `}</style>

      <Box style={{ background: t.bg, color: t.text, minHeight: '100vh', fontFamily: body, overflowX: 'hidden' }}>
        <SimpleNavbar
          showThemeToggle={false}
          showDropdownTheme={true}
          showAuthButtons={!isAuthenticated}
          userRole={userRole}
          userInfo={userInfo}
          onLogout={handleLogout}
        />

        {/* ============================= HERO ============================= */}
        <Box
          component="section"
          style={{
            position: 'relative',
            overflow: 'hidden',
            borderBottom: `1px solid ${t.border}`,
          }}
        >
          {/* Acento sutil de fondo (no blob agresivo) */}
          <Box
            aria-hidden
            style={{
              position: 'absolute',
              top: '-30%',
              right: '-10%',
              width: 520,
              height: 520,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${t.accent}1f, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />
          <Container
            size="lg"
            pb={{ base: 56, md: 96 }}
            style={{ position: 'relative', zIndex: 1, paddingTop: 'clamp(120px, 16vw, 150px)' }}
          >
            <Stack gap={28} align="center" ta="center" className="lp-rise">
              <Badge
                variant="light"
                radius="sm"
                styles={{
                  root: {
                    background: dark ? 'rgba(196,213,70,0.12)' : 'rgba(196,213,70,0.18)',
                    color: t.brand,
                    border: `1px solid ${t.accent}`,
                    fontFamily: body,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'none',
                    paddingInline: 14,
                    paddingBlock: 8,
                  },
                }}
              >
                Plataforma de preparación para el ENARM
              </Badge>

              <Title
                order={1}
                style={{
                  fontFamily: display,
                  fontSize: 'clamp(2.3rem, 6vw, 4rem)',
                  fontWeight: 700,
                  lineHeight: 1.08,
                  letterSpacing: '-0.02em',
                  maxWidth: 820,
                  textWrap: 'balance',
                  color: t.text,
                }}
              >
                Aquí comienza el camino de tu{' '}
                <Box
                  component="span"
                  style={{
                    position: 'relative',
                    color: t.brand,
                    display: 'inline-block',
                  }}
                >
                  especialización
                  <Box
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: '0.06em',
                      height: '0.34em',
                      background: t.accent,
                      opacity: 0.55,
                      borderRadius: 4,
                      zIndex: -1,
                    }}
                  />
                </Box>
              </Title>

              <Text
                style={{
                  fontFamily: body,
                  fontSize: 'clamp(1rem, 2.2vw, 1.18rem)',
                  lineHeight: 1.6,
                  color: t.muted,
                  maxWidth: 640,
                }}
              >
                Más de <b style={{ color: t.text }}>27 especialidades</b>, simuladores personalizables,
                medición de progreso, asistencia y recomendaciones con base en tus necesidades.
              </Text>

              <Group gap="md" justify="center" mt={8}>
                <Button
                  size="md"
                  radius="md"
                  onClick={scrollToPrecios}
                  rightSection={<IconArrowRight size={18} />}
                  styles={{
                    root: {
                      background: t.ctaBg,
                      color: t.ctaText,
                      fontFamily: body,
                      fontWeight: 600,
                      height: 48,
                      paddingInline: 26,
                    },
                  }}
                >
                  Conoce tus planes
                </Button>
                {!isAuthenticated && (
                  <Button
                    size="md"
                    radius="md"
                    variant="default"
                    onClick={() => navigate('/register')}
                    styles={{
                      root: {
                        background: 'transparent',
                        color: t.text,
                        border: `1px solid ${t.border}`,
                        fontFamily: body,
                        fontWeight: 600,
                        height: 48,
                        paddingInline: 26,
                      },
                    }}
                  >
                    Crear cuenta
                  </Button>
                )}
              </Group>

              {/* Stats */}
              <SimpleGrid
                cols={{ base: 2, sm: 4 }}
                spacing={{ base: 20, sm: 40 }}
                mt={40}
                style={{ width: '100%', maxWidth: 680 }}
              >
                {STATS.map((s) => (
                  <Stack key={s.label} gap={2} align="center">
                    <Text
                      style={{
                        fontFamily: display,
                        fontSize: 'clamp(1.7rem, 4vw, 2.3rem)',
                        fontWeight: 700,
                        color: t.brand,
                        lineHeight: 1,
                      }}
                    >
                      {s.number}
                    </Text>
                    <Text
                      style={{
                        fontFamily: body,
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: t.muted,
                      }}
                    >
                      {s.label}
                    </Text>
                  </Stack>
                ))}
              </SimpleGrid>
            </Stack>
          </Container>
        </Box>

        {/* ============================= FEATURES ============================= */}
        <Container component="section" size="lg" py={{ base: 56, md: 88 }}>
          <Stack gap={12} align="center" ta="center" mb={48}>
            {sectionLabel('Funcionalidades premium')}
            <Title
              order={2}
              style={{
                fontFamily: display,
                fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: t.text,
                textWrap: 'balance',
                maxWidth: 680,
              }}
            >
              Exámenes personalizados y contenido especializado
            </Title>
          </Stack>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={28}>
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <Box
                  key={f.title}
                  className="lp-card"
                  p={28}
                  style={{
                    background: t.surface,
                    border: `1px solid ${t.border}`,
                    borderRadius: 16,
                  }}
                >
                  <Stack gap={16}>
                    <Center
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        background: dark ? 'rgba(196,213,70,0.12)' : 'rgba(196,213,70,0.18)',
                      }}
                    >
                      <Icon size={26} color={t.brand} stroke={1.6} />
                    </Center>
                    <Title
                      order={3}
                      style={{
                        fontFamily: display,
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: t.text,
                      }}
                    >
                      {f.title}
                    </Title>
                    <Text style={{ fontFamily: body, fontSize: '0.98rem', lineHeight: 1.6, color: t.muted }}>
                      {f.description}
                    </Text>
                  </Stack>
                </Box>
              );
            })}
          </SimpleGrid>
        </Container>

        {/* ============================= PRICING ============================= */}
        <Box
          component="section"
          id="precios"
          style={{ background: t.surfaceAlt, borderBlock: `1px solid ${t.border}` }}
        >
          <Container size="lg" py={{ base: 56, md: 88 }}>
            <Stack gap={12} align="center" ta="center" mb={48}>
              {sectionLabel('Planes de suscripción')}
              <Title
                order={2}
                style={{
                  fontFamily: display,
                  fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: t.text,
                }}
              >
                Elige tu plan
              </Title>
              <Text style={{ fontFamily: body, fontSize: '1.02rem', color: t.muted, maxWidth: 560 }}>
                Elige el plan que mejor se adapte a tus necesidades de preparación para el ENARM.
              </Text>
            </Stack>

            {loading ? (
              <Center py={60}>
                <Loader color={t.brand} />
              </Center>
            ) : error ? (
              <Center py={60}>
                <Text style={{ fontFamily: body, color: t.muted }}>{error}</Text>
              </Center>
            ) : (
              <SimpleGrid
                cols={{ base: 1, sm: 2, md: Math.min(plans.length || 3, 3) }}
                spacing="xl"
                style={{ maxWidth: 1040, marginInline: 'auto', alignItems: 'stretch', paddingTop: 18 }}
              >
                {plans.map((plan, index) => {
                  const isPopular = index === popularIndex;
                  return (
                    <Box
                      key={plan.id}
                      className="lp-card"
                      style={{
                        position: 'relative',
                        background: t.surface,
                        border: isPopular ? `2px solid ${t.brand}` : `1px solid ${t.border}`,
                        borderRadius: 18,
                        padding: '32px 26px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: isPopular ? '0 14px 34px rgba(54, 71, 91, 0.12)' : 'none',
                      }}
                    >
                      {isPopular && (
                        <Badge
                          size="lg"
                          leftSection={<IconStar size={12} />}
                          styles={{
                            root: {
                              position: 'absolute',
                              top: -14,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: t.brand,
                              color: '#fff',
                              fontFamily: body,
                              fontWeight: 600,
                              textTransform: 'none',
                              whiteSpace: 'nowrap',
                              height: 28,
                              paddingInline: 16,
                            },
                            label: { overflow: 'visible' },
                          }}
                        >
                          Más popular
                        </Badge>
                      )}

                      <Stack gap={6} align="center" mt={isPopular ? 8 : 0}>
                        <Text
                          style={{
                            fontFamily: display,
                            fontSize: '1.15rem',
                            fontWeight: 600,
                            color: t.text,
                          }}
                        >
                          {plan.name}
                        </Text>
                        <Group gap={4} align="baseline" justify="center">
                          <Text
                            style={{
                              fontFamily: display,
                              fontSize: '2.8rem',
                              fontWeight: 700,
                              color: t.brand,
                              lineHeight: 1,
                            }}
                          >
                            ${Number(plan.price)}
                          </Text>
                          <Text style={{ fontFamily: body, fontSize: '0.95rem', color: t.muted }}>
                            /{intervalLabel(plan.billingInterval)}
                          </Text>
                        </Group>
                        {plan.description && (
                          <Text
                            ta="center"
                            style={{ fontFamily: body, fontSize: '0.9rem', color: t.muted, marginTop: 4 }}
                          >
                            {plan.description}
                          </Text>
                        )}
                      </Stack>

                      {(plan.features || []).length > 0 && (
                        <List
                          spacing="sm"
                          mt={24}
                          mb={24}
                          center
                          icon={
                            <Center
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                background: dark ? 'rgba(196,213,70,0.15)' : 'rgba(196,213,70,0.22)',
                              }}
                            >
                              <IconChecks size={12} color={t.brand} />
                            </Center>
                          }
                          style={{ flex: 1 }}
                        >
                          {(plan.features || []).map((feature, i) => (
                            <List.Item key={i}>
                              <Text style={{ fontFamily: body, fontSize: '0.92rem', color: t.text }}>
                                {feature}
                              </Text>
                            </List.Item>
                          ))}
                        </List>
                      )}

                      <Button
                        fullWidth
                        radius="md"
                        mt="auto"
                        onClick={() => navigate(isAuthenticated ? '/mejorarplan' : '/register')}
                        styles={{
                          root: {
                            background: isPopular ? t.brand : 'transparent',
                            color: isPopular ? '#fff' : t.text,
                            border: isPopular ? 'none' : `1px solid ${t.border}`,
                            fontFamily: body,
                            fontWeight: 600,
                            height: 46,
                          },
                        }}
                      >
                        Comenzar
                      </Button>
                    </Box>
                  );
                })}
              </SimpleGrid>
            )}
          </Container>
        </Box>

        {/* ============================= FAQ ============================= */}
        <Container component="section" size="sm" py={{ base: 56, md: 88 }}>
          <Stack gap={12} align="center" ta="center" mb={40}>
            {sectionLabel('Preguntas frecuentes')}
            <Title
              order={2}
              style={{
                fontFamily: display,
                fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: t.text,
              }}
            >
              Resolvemos tus dudas
            </Title>
          </Stack>

          <Accordion
            variant="separated"
            radius="md"
            styles={{
              item: {
                background: t.surface,
                border: `1px solid ${t.border}`,
                marginBottom: 12,
              },
              control: { fontFamily: display, fontWeight: 600, color: t.text },
              label: { fontSize: '1rem' },
              panel: { fontFamily: body },
              content: { color: t.muted, fontSize: '0.95rem', lineHeight: 1.6 },
              chevron: { color: t.brand },
            }}
          >
            {FAQS.map((faq) => (
              <Accordion.Item key={faq.value} value={faq.value}>
                <Accordion.Control>{faq.q}</Accordion.Control>
                <Accordion.Panel>{faq.a}</Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>

        {/* ============================= FOOTER ============================= */}
        <Box component="footer" style={{ background: t.surface, borderTop: `1px solid ${t.border}` }}>
          <Container size="lg" py={{ base: 48, md: 64 }}>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
              {/* Marca */}
              <Stack gap={16} maw={420}>
                <Group gap={12}>
                  <img src={enarmLogo} alt="ENARM360" width={40} height={40} style={{ objectFit: 'contain' }} />
                  <Text style={{ fontFamily: display, fontSize: '1.25rem', fontWeight: 700, color: t.text }}>
                    ENARM360
                  </Text>
                </Group>
                <Text style={{ fontFamily: display, fontSize: '1.05rem', fontWeight: 600, color: t.text }}>
                  El futuro de la medicina empieza con tu preparación hoy.
                </Text>
                <Text style={{ fontFamily: body, fontSize: '0.92rem', lineHeight: 1.6, color: t.muted }}>
                  Plataforma líder en preparación para el ENARM, con más de 15,000 reactivos actualizados y
                  herramientas de última tecnología para tu éxito.
                </Text>
              </Stack>

              {/* Contacto */}
              <Stack gap={14} align="flex-start">
                <Text
                  style={{
                    fontFamily: body,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: t.muted,
                  }}
                >
                  Contacto
                </Text>
                <Anchor
                  href="mailto:enarm360@gmail.com"
                  style={{ fontFamily: body, color: t.text, textDecoration: 'none' }}
                >
                  <Group gap={8}>
                    <IconMail size={18} color={t.brand} />
                    <Text style={{ fontFamily: body, color: t.text }}>enarm360@gmail.com</Text>
                  </Group>
                </Anchor>
                <Anchor
                  href="https://www.instagram.com/enarm360?igsh=Y3E3am5oZzU3cmNr&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontFamily: body, color: t.text, textDecoration: 'none' }}
                >
                  <Group gap={8}>
                    <IconBrandInstagram size={18} color={t.brand} />
                    <Text style={{ fontFamily: body, color: t.text }}>@enarm360</Text>
                  </Group>
                </Anchor>
              </Stack>
            </SimpleGrid>

            <Box my={32} style={{ height: 1, background: t.border }} />

            <Group justify="space-between" gap="md" style={{ flexWrap: 'wrap' }}>
              <Text style={{ fontFamily: body, fontSize: '0.82rem', color: t.muted, maxWidth: 560 }}>
                © {new Date().getFullYear()} ENARM360. Plataforma especializada en simulacros para el Examen
                Nacional de Aspirantes a Residencias Médicas. Todos los derechos reservados.
              </Text>
              <Group gap={20}>
                <Text style={{ fontFamily: body, fontSize: '0.82rem', color: t.muted }}>
                  Términos y Condiciones
                </Text>
                <Text style={{ fontFamily: body, fontSize: '0.82rem', color: t.muted }}>
                  Política de Privacidad
                </Text>
              </Group>
            </Group>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default LandingPage;
